import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  CollapsibleState,
  isCollapsibleExpanded,
} from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeItemContext,
} from "~/components/build/Show/routes/list/-components/TreeNode";
import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { CollapsibleStepItem } from "~/components/build/Show/routes/list/-components/TreeNode/CollapsibleStepItem";
import { createCommandJobNode } from "~/components/build/Show/routes/list/-components/TreeNode/CommandJobItem";
import { createShowHiddenJobsNode } from "~/components/build/Show/routes/list/-components/TreeNode/CommandStepExpandItem";
import { filterStepJobs } from "~/components/build/Show/lib/useFilterStore";
import { useBuild, mostRecentParallelJobs } from "~/components/build/Show/lib/BuildContext";
import { sortJobsByState } from "~/components/build/Show/lib/sortJobsByState";
import { pastAttemptsForJob } from "~/components/build/Show/lib/jobRetryHistory";
import { Build } from "~/components/build/Show/lib/types";
import { ParallelCommandJob } from "~/components/build/Show/lib/types/CommandJob";
import { useBuildPageStore } from "~/components/build/Show/lib/useBuildPageStore";
import { CommandStep } from "~/lib/pipeline";
import { stepJobsQueryOptions } from "~/components/build/Show/lib/queries/stepJobs";
import { FilterByOption } from "~/components/build/Show/lib/filterConstants";

const ParallelStepItem = ({ step, ...props }: { step: CommandStep } & TreeItemContext) => {
  const { build } = useBuild();
  const setJobs = useBuildPageStore((state) => state.setJobs);

  const query = useQuery(
    stepJobsQueryOptions(build, step.uuid, {
      enabled: isCollapsibleExpanded(props.collapsibleState),
    }),
  );

  useEffect(() => {
    if (query.data) {
      setJobs(query.data);
    }
  }, [query.dataUpdatedAt]);

  return (
    <CollapsibleStepItem
      {...props}
      step={step}
      isLoading={query.isLoading}
      isLoaded={query.isSuccess}
    />
  );
};

export const createParallelStepNode = (step: CommandStep, build: Build): TreeNode => ({
  id: step.uuid,
  height: STEP_ITEM_HEIGHT,
  sticky: true,
  render: (props) => <ParallelStepItem step={step} {...props} />,
  childNodes: (
    collapsibleState: CollapsibleState = CollapsibleState.Collapsed,
    filters: FilterByOption[] = [],
  ) => {
    if (collapsibleState === CollapsibleState.Collapsed) {
      return [];
    }

    const allStepJobs = (useBuildPageStore.getState().stepJobs.get(step.uuid) ||
      []) as ParallelCommandJob[];
    const jobs = mostRecentParallelJobs(allStepJobs);

    const items: TreeNode[] = [];
    if (jobs.length > 0) {
      const filteredJobs =
        collapsibleState === CollapsibleState.Full && filters.length === 0
          ? [...jobs]
          : filterStepJobs(jobs, step, build);

      filteredJobs.sort(sortJobsByState("asc"));

      for (const job of filteredJobs) {
        const hasRetryHistory =
          Features.BuildListJobRetryHistory && pastAttemptsForJob(allStepJobs, job).length > 0;
        items.push(createCommandJobNode(step, job, hasRetryHistory));
      }

      const hiddenCount = jobs.length - filteredJobs.length;

      if (hiddenCount > 0 && filters.length === 0) {
        items.push(createShowHiddenJobsNode(step, hiddenCount));
      }
    }

    return items;
  },
});
